/* eslint-disable camelcase */
/** 3rd party */
import React, { useEffect, useState } from "react";
import { GetServerSidePropsContext, InferGetServerSidePropsType } from "next";
import getConfig from "next/config";
import { NextSeo } from "next-seo";
/** 3rd party @themuse */
import {
    PageLayout,
    JobSearchRequest,
    JobSearchResults,
    useCompanySearch,
    ComposableHeaderDropdownProviderProps,
} from "@themuse/design-system";
import { getExperiments, Experiment, ExperimentProvider, getActiveVariant, IExperiment } from "@themuse/experiment-client";
import { useInView } from "react-intersection-observer";
/** Interfaces */
import { AppState } from "interfaces/context/appContextInterfaces";
import { JobLevel, SingleCompany, SingleJob } from "interfaces/api/apiInterfaces";
/** utils */
import { getActiveFeatureFlags } from "utils/featureFlags";
import { getCompressedJob } from "utils/orm";
import { logger, formatLog, wrapError } from "utils/logging";
import { newsletterSubscribeRequest, searchJobs } from "utils/api";
import { applyButtonTextConfig } from "utils/ApplyButtonTextExperiment";
/** hooks */
import { AppProvider } from "hooks/AppContext";
import { FeatureFlagsProvider } from "hooks/FeatureFlagsContext";
/** components */
import { JobIndividualPageLayout } from "components/JobIndividualPageLayout";
import { JobIndividualHeader } from "components/JobIndividualHeader";
import { JobIndividualBody } from "components/JobIndividualBody";
import { JobIndividualSidebar } from "components/JobIndividualSidebar";
import { JobPostJsonLd } from "components/JobPostJsonLd";
import { UserInit } from "components/UserInit";
import { StickyHeader } from "components/StickyHeader";
/** styles */
import styles from "styles/Home.module.scss";


interface InitialAppParams extends AppState {
    redirectPath?: string;
    queryString?: string;
    experiments: IExperiment[];
    enabledVariant: string | null;
    archived: boolean;
    isJob: boolean;
    isFramed: boolean;
    job?: SingleJob;
    company?: SingleCompany;
}

/**
 * Retrieve a list of similar jobs based on the input job.
 */
export const getSimilarJobs = async (job: SingleJob): Promise<JobSearchResults | null> => {
    const { name = "", levels = [] } = job || {};

    const similarJobsRequest: JobSearchRequest = {
        query: name?.split(" ").slice(0, 3).join(" ") ?? "",
        level: levels?.map((level: JobLevel) => level?.value === "senior" ? "senior,management" : level?.value)?.join(",") ?? null,
        limit: 4
    };

    try {
        const similarJobs = await searchJobs(similarJobsRequest);
        return similarJobs;
    } catch (error) {
        const msg = `Error requesting similar jobs for job '${name}' at ${error?.request?.path}.`;
        const e = wrapError(msg, error)
        logger.log(formatLog({ error: e, level: "warn" }));
    }
    return null;
};

/**
 * Experiment configurations.
 */
const experimentConfig: Experiment[] = applyButtonTextConfig;

/**
 * Checks if the job or company is not found.
 */
const checkNotFound = (initialAppParams: InitialAppParams) => {
    const { job, company, redirectPath } = initialAppParams;
    return (!job || !company) && !redirectPath;
};

/**
 * Checks if a job is archived.
 */
const checkArchived = (job) => {
    return job?.state === "archived";
};

/**
 * Generates a redirect configuration object based on the initial application parameters.
 * If a redirect path is provided, constructs the destination URL with the query string if available.
 */
const getRedirectConfig = (initialAppParams: InitialAppParams) => {
    return initialAppParams.redirectPath ? {
        redirect: {
            destination: initialAppParams.queryString
                ? `${initialAppParams.redirectPath}?${initialAppParams.queryString}`
                : initialAppParams.redirectPath,
            permanent: true
        }
    } : {};
}

/**
 * Retrieves initial application parameters including experiments, variant, job, company, similar jobs, and archived.
 */
export const getInitialAppParams = async (context: GetServerSidePropsContext): Promise<InitialAppParams> => {
    const { publicRuntimeConfig: { activeExperiments } } = getConfig();
    const experiments: IExperiment[] = getExperiments(experimentConfig, context, activeExperiments) ?? [];

    const { job, company } = await getCompressedJob(context);
    const directApplyService = job?.ats_info?.directApplyService ?? "";
    const archived: boolean = checkArchived(job);
    const similarJobs: JobSearchResults | null = await getSimilarJobs(job);
    const experimentId = directApplyService ? "DIRECT_APPLY_CTA_COPY" : "APPLY_CTA_COPY";
    const enabledVariant: string = getActiveVariant(experimentId, experiments) ?? "";

    return {
        job,
        company,
        similarJobs,
        experiments,
        enabledVariant,
        archived,
        isJob: true,
        isFramed: false
    };
};

/**
 * Handles an archived job by setting the response status code to 404 and logging a custom message.
 */
const handleArchivedJob = (context: GetServerSidePropsContext) => {
    const msg = "Intentionally returned an archived job as a custom 404.";
    logger.log(formatLog({ context, contextToLogMap: null, error: new Error(msg), level: "debug" }));
    context.res.statusCode = 404;
};

/**
 * Retrieves job and company data from the server-side API and passes it as props to the 'Home' component.
 */
export const getServerSideProps = async (context: GetServerSidePropsContext): Promise<any> => {
    let initialAppParams: InitialAppParams = {} as unknown as InitialAppParams;
    let notFound: boolean;

    try {
        initialAppParams = await getInitialAppParams(context);
    } catch (error) {
        // If we cant get initial params set not found.
        notFound = true;
        logger.log(formatLog({ error, level: "error" }));
    }

    const { archived = false, experiments = [] } = initialAppParams;

    if (archived) {
        handleArchivedJob(context);
    }

    const redirectConfig = getRedirectConfig(initialAppParams);

    // Overwrite the cookie as we may have forced one in the URL within getInitialAppParams.
    const cookieValue = encodeURIComponent(JSON.stringify(experiments));
    context.res.setHeader("Set-Cookie", `tm_experiment=${cookieValue};expires=2592000;path=/`);

    return {
        notFound: notFound || checkNotFound(initialAppParams),
        ...redirectConfig,
        props: {
            statusCode: context.res.statusCode,
            initialAppParams,
            featureFlags: {
                ...getActiveFeatureFlags(context)
            },
            experiments,
        }
    };
};

/**
 * Compiles the props object for the page layout component.
 */
const getPageLayoutProps = (companySearchDropdownProps: ComposableHeaderDropdownProviderProps) => ({
    activePath: "/search",
    headerClassName: styles.pageHeader,
    pageName: styles.pageWrapper,
    mainClassName: styles.main,
    enableCompanySearch: true,
    companySearchDropdownProps,
    searchUrl: "/search",
    showSubMenus: true,
    handleModalFormSubmit: newsletterSubscribeRequest
});

/**
 * Config for triggering inView value for the sticky nav.
 */
const inViewConfig = {
    triggerOnce: false,
    root: null,
    rootMargin: '0px',
    threshold: 0.9
};

/**
 * Retrieves server-side props for the page, including experiments, job and company details, similar jobs, and feature flags.
 * Handles cases of archived jobs, not found pages, and redirects based on initial app parameters.
 * Returns an object with notFound status, redirect configuration, and props containing necessary data for the page.
 */
const Home = (props: InferGetServerSidePropsType<typeof getServerSideProps>) => {
    const [isSticky, setIsSticky] = useState<boolean>(false);
    const [isDirectApplyOpen, setIsDirectApplyOpen] = React.useState<boolean>(false);
    const { publicRuntimeConfig: { museWebHost, apiBase } } = getConfig();
    const { ref, inView } = useInView(inViewConfig);
    const { initialAppParams, featureFlags, experiments } = props;
    const companySearchDropdownProps = useCompanySearch({ fetchConfig: { apiPath: `${apiBase}company-suggestion` }});

    const { job, company } = initialAppParams;
    const {
        ats_info,
        id,
        locations = [],
        apply_link = "",
        categories = [],
        levels = [],
        state = "",
        name: jobName = "",
        normalizedTitle = "",
        short_name: jobShortName = "",
    } = job;
    const {
        name: companyName = "",
        short_name: companyShortName = "",
        legacy_id = ""
    } = company;

    const { directApplyPath = "", directApplyService = "" } = ats_info || {};
    const physicalLocations = locations.filter((location) => location?.value !== "Flexible / Remote");
    const firstLocation = physicalLocations?.[0]?.value ?? "";
    const firstlocationDisplay = firstLocation ? ` located in ${firstLocation}, ` : ", ";
    const canonical = `${museWebHost}/jobs/${companyShortName}/${jobShortName}`;
    const descriptionEnd = "as well as other career opportunities that the company is hiring for."
    const description = `Find our ${jobName} job description for ${companyName}${firstlocationDisplay} ${descriptionEnd}`;
    const noIndex = state !== "live";
    const title = `${jobName} at ${companyName} | The Muse`;
    const setIsOpen = directApplyService ? setIsDirectApplyOpen : null;
    const pageLayoutProps: React.ComponentProps<typeof PageLayout> = getPageLayoutProps(companySearchDropdownProps);

    const directApplyProps = {
        companyId: legacy_id + "",
        companyName: companyName,
        endpoint: directApplyPath,
        jobId: id,
        jobCategory: categories?.[0],
        normalizedTitle: normalizedTitle,
        level: levels?.[0]?.value ?? "",
        jobName,
        link: apply_link,
        locations,
        atsJobId: ats_info?.id ?? "",
        service: directApplyService
    };

    useEffect(() => {
        setIsSticky(!inView)
    }, [inView]);

    return (
        <FeatureFlagsProvider featureFlags={featureFlags}>
            <AppProvider {...initialAppParams}>
                <NextSeo {...{
                    title,
                    description,
                    canonical,
                    openGraph: {
                        url: canonical,
                        title,
                        description,
                    },
                    noindex: noIndex
                }}
                />
                <JobPostJsonLd />
                <UserInit>
                    <JobIndividualPageLayout {...pageLayoutProps}>
                        <ExperimentProvider experiments={experiments}>
                            <StickyHeader setIsOpen={setIsOpen} isSticky={isSticky} />
                            <JobIndividualHeader innerRef={ref} setIsOpen={setIsOpen} />
                            <JobIndividualBody setIsOpen={setIsOpen} />
                            <JobIndividualSidebar setIsOpen={setIsOpen} />
                        </ExperimentProvider>
                    </JobIndividualPageLayout>
                </UserInit>
            </AppProvider>
        </FeatureFlagsProvider>
    );
};

export default Home;
